import React, { useState, useEffect } from 'react';
import { 
    TrendingUp, TrendingDown, DollarSign, ShoppingBag, 
    Package, Loader2, Calendar, BarChart3, PieChart,
    ArrowUpRight, ArrowDownRight, Coins, CalendarRange
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Popover,
    PopoverContent,
    PopoverTrigger 
} from '../components/ui/popover';
import { apiRequest, formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart as RechartsPie,
    Pie,
    Cell,
    Legend
} from 'recharts';

const COLORS = ['#0077be', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function SalesReportPage() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [showCustomRange, setShowCustomRange] = useState(false);

    useEffect(() => {
        if (period !== 'custom') {
            loadReport();
        }
    }, [period]);

    const loadReport = async (customStart = null, customEnd = null) => {
        setLoading(true);
        try {
            let url = `/admin/sales-report?period=${period}`;
            if (period === 'custom' && customStart && customEnd) {
                url = `/admin/sales-report?period=custom&start_date=${customStart}&end_date=${customEnd}`;
            }
            const data = await apiRequest(url);
            setReport(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (value) => {
        if (value === 'custom') {
            setShowCustomRange(true);
            setPeriod('custom');
        } else {
            setShowCustomRange(false);
            setPeriod(value);
        }
    };

    const applyCustomRange = () => {
        if (!customStartDate || !customEndDate) {
            toast.error('Veuillez sélectionner une date de début et de fin');
            return;
        }
        if (new Date(customStartDate) > new Date(customEndDate)) {
            toast.error('La date de début doit être antérieure à la date de fin');
            return;
        }
        loadReport(customStartDate, customEndDate);
    };

    const getPeriodLabel = () => {
        if (period === 'custom' && report?.date_range) {
            const start = new Date(report.date_range.start).toLocaleDateString('fr-FR');
            const end = new Date(report.date_range.end).toLocaleDateString('fr-FR');
            return `Du ${start} au ${end}`;
        }
        switch (period) {
            case 'day': return "Aujourd'hui";
            case 'week': return '7 derniers jours';
            case 'year': return '12 derniers mois';
            default: return '30 derniers jours';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!report) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                Impossible de charger le rapport
            </div>
        );
    }

    const { summary, chart_data, top_products, payment_methods } = report;

    // Prepare payment methods data for pie chart
    const paymentData = Object.entries(payment_methods || {}).map(([method, data]) => ({
        name: method === 'stripe' ? 'Carte bancaire' : 
              method === 'apple_google_pay' ? 'Apple/Google Pay' : method,
        value: data.revenue,
        count: data.count
    }));

    return (
        <div className="space-y-6" data-testid="sales-report">
            {/* Header with period selector */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Rapport de ventes</h2>
                    <p className="text-muted-foreground">{getPeriodLabel()}</p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={period} onValueChange={handlePeriodChange}>
                        <SelectTrigger className="w-48" data-testid="period-selector">
                            <Calendar className="h-4 w-4 mr-2" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="day">Aujourd&apos;hui</SelectItem>
                            <SelectItem value="week">7 jours</SelectItem>
                            <SelectItem value="month">30 jours</SelectItem>
                            <SelectItem value="year">12 mois</SelectItem>
                            <SelectItem value="custom">Plage personnalisée</SelectItem>
                        </SelectContent>
                    </Select>

                    {showCustomRange && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="custom-range-btn">
                                    <CalendarRange className="h-4 w-4 mr-2" />
                                    Définir dates
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80" align="end">
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm">Sélectionner la plage de dates</h4>
                                    <div className="grid gap-3">
                                        <div className="grid gap-1.5">
                                            <Label htmlFor="start-date">Date de début</Label>
                                            <Input
                                                id="start-date"
                                                type="date"
                                                value={customStartDate}
                                                onChange={(e) => setCustomStartDate(e.target.value)}
                                                data-testid="start-date-input"
                                            />
                                        </div>
                                        <div className="grid gap-1.5">
                                            <Label htmlFor="end-date">Date de fin</Label>
                                            <Input
                                                id="end-date"
                                                type="date"
                                                value={customEndDate}
                                                onChange={(e) => setCustomEndDate(e.target.value)}
                                                data-testid="end-date-input"
                                            />
                                        </div>
                                    </div>
                                    <Button 
                                        onClick={applyCustomRange} 
                                        className="w-full"
                                        data-testid="apply-range-btn"
                                    >
                                        Appliquer
                                    </Button>
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Revenue */}
                <Card className="glass">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Chiffre d&apos;affaires</p>
                                <p className="text-2xl font-bold">{formatPrice(summary.total_revenue)}</p>
                            </div>
                            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                                summary.revenue_change_percent >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'
                            }`}>
                                <DollarSign className={`h-6 w-6 ${
                                    summary.revenue_change_percent >= 0 ? 'text-emerald-500' : 'text-red-500'
                                }`} />
                            </div>
                        </div>
                        <div className={`flex items-center gap-1 mt-2 text-sm ${
                            summary.revenue_change_percent >= 0 ? 'text-emerald-500' : 'text-red-500'
                        }`}>
                            {summary.revenue_change_percent >= 0 ? (
                                <ArrowUpRight className="h-4 w-4" />
                            ) : (
                                <ArrowDownRight className="h-4 w-4" />
                            )}
                            <span>{Math.abs(summary.revenue_change_percent)}% vs période précédente</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Orders */}
                <Card className="glass">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Commandes</p>
                                <p className="text-2xl font-bold">{summary.total_orders}</p>
                            </div>
                            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                                summary.orders_change_percent >= 0 ? 'bg-blue-500/10' : 'bg-red-500/10'
                            }`}>
                                <ShoppingBag className={`h-6 w-6 ${
                                    summary.orders_change_percent >= 0 ? 'text-blue-500' : 'text-red-500'
                                }`} />
                            </div>
                        </div>
                        <div className={`flex items-center gap-1 mt-2 text-sm ${
                            summary.orders_change_percent >= 0 ? 'text-emerald-500' : 'text-red-500'
                        }`}>
                            {summary.orders_change_percent >= 0 ? (
                                <ArrowUpRight className="h-4 w-4" />
                            ) : (
                                <ArrowDownRight className="h-4 w-4" />
                            )}
                            <span>{Math.abs(summary.orders_change_percent)}% vs période précédente</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Average Order */}
                <Card className="glass">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Panier moyen</p>
                                <p className="text-2xl font-bold">{formatPrice(summary.avg_order_value)}</p>
                            </div>
                            <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <BarChart3 className="h-6 w-6 text-purple-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Points Used */}
                <Card className="glass">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Points fidélité utilisés</p>
                                <p className="text-2xl font-bold">{formatPrice(summary.points_discount_used)}</p>
                            </div>
                            <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                                <Coins className="h-6 w-6 text-amber-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Revenue Chart */}
                <Card className="glass">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            Évolution du chiffre d&apos;affaires
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {chart_data.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chart_data}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0077be" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#0077be" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={(value) => {
                                            if (period === 'year') return value.slice(5);
                                            if (period === 'day') return value;
                                            return value.slice(5);
                                        }}
                                    />
                                    <YAxis 
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={(value) => `${value}€`}
                                    />
                                    <Tooltip 
                                        formatter={(value) => [`${value}€`, 'CA']}
                                        labelFormatter={(label) => `Date: ${label}`}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="revenue" 
                                        stroke="#0077be" 
                                        fillOpacity={1} 
                                        fill="url(#colorRevenue)" 
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                Aucune donnée pour cette période
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Orders Chart */}
                <Card className="glass">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <ShoppingBag className="h-5 w-5 text-blue-500" />
                            Nombre de commandes
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {chart_data.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chart_data}>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={(value) => {
                                            if (period === 'year') return value.slice(5);
                                            if (period === 'day') return value;
                                            return value.slice(5);
                                        }}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip 
                                        formatter={(value) => [value, 'Commandes']}
                                    />
                                    <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                Aucune donnée pour cette période
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Bottom Row */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* Top Products */}
                <Card className="glass">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Package className="h-5 w-5 text-emerald-500" />
                            Produits les plus vendus
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {top_products.length > 0 ? (
                            <div className="space-y-4">
                                {top_products.slice(0, 5).map((product, index) => (
                                    <div key={product.product_id} className="flex items-center gap-4">
                                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                                            {index + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{product.name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {product.quantity} vendus
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold">{formatPrice(product.revenue)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-muted-foreground py-8">
                                Aucune vente pour cette période
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Payment Methods */}
                <Card className="glass">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <PieChart className="h-5 w-5 text-purple-500" />
                            Méthodes de paiement
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {paymentData.length > 0 ? (
                            <div className="flex items-center gap-8">
                                <ResponsiveContainer width="50%" height={200}>
                                    <RechartsPie>
                                        <Pie
                                            data={paymentData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {paymentData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => formatPrice(value)} />
                                    </RechartsPie>
                                </ResponsiveContainer>
                                <div className="flex-1 space-y-3">
                                    {paymentData.map((method, index) => (
                                        <div key={method.name} className="flex items-center gap-3">
                                            <div 
                                                className="h-3 w-3 rounded-full" 
                                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                            />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium">{method.name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {method.count} commandes
                                                </p>
                                            </div>
                                            <p className="font-semibold text-sm">{formatPrice(method.value)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center text-muted-foreground py-8">
                                Aucune donnée de paiement
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
